import {
    createTheme,
    Theme as MuiTheme,
    ThemeProvider as MuiThemeProvider,
} from '@mui/material';
import * as colors from '@mui/material/colors';
import CssBaseline from '@mui/material/CssBaseline';
// Pulls the x-date-pickers component keys (MuiPickersDay/Year/Month) into the
// theme's `components` type so we can style the calendar popup below.
import type {} from '@mui/x-date-pickers/themeAugmentation';
import * as React from 'react';
import { useSetDarkModeMutation } from '../../../services';

export type ColorMode = 'light' | 'dark';

interface IColorModeContext {
    mode: ColorMode;
    toggleColorMode: () => void;
    // Set the mode directly, without persisting (used by DarkModeSync to apply
    // the server value on login). localStorage stays in sync for first paint.
    setMode: (mode: ColorMode) => void;
}

export const ColorModeContext = React.createContext<IColorModeContext>({
    mode: 'light',
    toggleColorMode: () => {},
    setMode: () => {},
});

const STORAGE_KEY = 'inopack-color-mode';

interface ICustomThemeProviderProps {
    children: React.ReactNode;
}

export const CustomThemeProvider = (props: ICustomThemeProviderProps) => {
    const [mode, setModeState] = React.useState<ColorMode>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === 'dark' ? 'dark' : 'light';
    });

    const [setDarkModeMutation] = useSetDarkModeMutation();

    // Apply a mode and cache it in localStorage for the next first paint, but do
    // NOT persist to the server — this is how the server value is applied on login.
    const setMode = React.useCallback((next: ColorMode) => {
        setModeState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // Private mode / storage disabled — the theme still works in-memory.
        }
    }, []);

    // User-initiated toggle: flip, cache locally, and persist to user_config. This
    // only ever fires from the navbar/drawer, i.e. while authenticated, so the
    // mutation always has a valid session.
    const toggleColorMode = React.useCallback(() => {
        setModeState((prev) => {
            const next: ColorMode = prev === 'light' ? 'dark' : 'light';
            try {
                localStorage.setItem(STORAGE_KEY, next);
            } catch {
                // ignore storage failures
            }
            void setDarkModeMutation({
                variables: { dark_mode: next === 'dark' },
            });
            return next;
        });
    }, [setDarkModeMutation]);

    const colorModeValue = React.useMemo(
        () => ({ mode, toggleColorMode, setMode }),
        [mode, toggleColorMode, setMode],
    );

    const muiTheme: MuiTheme = React.useMemo(() => {
        const theme = createTheme({
            palette: {
                mode,
                background: {
                    default: mode === 'light' ? colors['grey']['300'] : '#121212',
                },
            },
        });

        theme.components = {
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontWeight: 'inherit',
                        borderBottom: '1px solid transparent',
                        textAlign: 'center',
                        borderBottomColor: theme.palette.divider,
                        '&.MuiTableCell-head': {
                            fontWeight: 'bold',
                        },
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {},
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: theme.palette.background.paper,
                    },
                },
            },
            MuiListSubheader: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        lineHeight: '2rem',
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    clearIndicator: {
                        visibility: 'visible',
                        marginRight: 6,
                        marginBottom: 2,
                    },
                    popupIndicator: {
                        marginRight: 0,
                        marginBottom: 2,
                    },
                },
            },
            // Calendar popup: the keyboard "cursor" cell (the day/month/year the
            // arrow keys land on before you pick it) only gets MUI's default
            // `Mui-focusVisible`, a very low-opacity tint that's nearly invisible
            // against the paper. Give that focused cell a solid primary outline
            // so the selection cursor reads clearly. This is separate from the
            // already-styled "today" and "selected" states.
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: -2,
                            backgroundColor: theme.palette.action.selected,
                        },
                    },
                },
            },
            MuiPickersYear: {
                styleOverrides: {
                    yearButton: {
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: -2,
                            backgroundColor: theme.palette.action.selected,
                        },
                    },
                },
            },
            MuiPickersMonth: {
                styleOverrides: {
                    monthButton: {
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: -2,
                            backgroundColor: theme.palette.action.selected,
                        },
                    },
                },
            },
        };

        return theme;
    }, [mode]);

    return (
        <ColorModeContext.Provider value={colorModeValue}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                {props.children}
            </MuiThemeProvider>
        </ColorModeContext.Provider>
    );
};
